<template>
  <div
    :class="zoneClass"
    @click="openPicker"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="input"
      :class="$style.hiddenInput"
      type="file"
      accept=".jpg,.jpeg,.png,.tiff,.tif,.pdf"
      @change="onChange"
    />

    <div v-if="file" :class="$style.fileInfo">
      <img v-if="previewUrl" :src="previewUrl" :class="$style.previewImg" :alt="file.name" />
      <span v-else :class="$style.fileIcon">PDF</span>
      <span :class="$style.fileName">{{ file.name }}</span>
      <span :class="$style.fileSize">{{ fileSizeMb }} MB</span>
      <button :class="$style.removeBtn" type="button" @click.stop="$emit('file-removed')">
        {{ deleteLabel }}
      </button>
    </div>

    <div v-else :class="$style.placeholder">
      <span :class="$style.uploadIcon">↑</span>
      <span :class="$style.uploadLabel">{{ label }}</span>
      <span :class="$style.uploadHint">{{ uploadHint }}</span>
      <span :class="$style.uploadFormats">{{ uploadFormats }}</span>
    </div>

    <span v-if="error" :class="$style.errorMsg">{{ error }}</span>
  </div>
</template>

<script>
export default {
  name: 'DropZone',

  props: {
    label: { type: String, required: true },
    file: { type: File, default: null },
    error: { type: String, default: '' },
    uploadHint: { type: String, default: 'Sleep of klik om te uploaden' },
    uploadFormats: { type: String, default: 'JPG, PNG, TIFF, PDF · max 10 MB' },
    deleteLabel: { type: String, default: 'Delete' },
  },

  data() {
    return { dragging: false, previewUrl: null };
  },

  watch: {
    file(newFile) {
      if (this.previewUrl) {
        URL.revokeObjectURL(this.previewUrl);
        this.previewUrl = null;
      }
      if (newFile && /\.(jpe?g|png)$/i.test(newFile.name)) {
        this.previewUrl = URL.createObjectURL(newFile);
      }
    },
  },

  beforeDestroy() {
    if (this.previewUrl) URL.revokeObjectURL(this.previewUrl);
  },

  computed: {
    zoneClass() {
      return [
        this.$style.zone,
        this.dragging && this.$style.zoneDragging,
        this.error && this.$style.zoneError,
        this.file && this.$style.zoneHasFile,
      ];
    },
    fileSizeMb() {
      return this.file ? (this.file.size / 1024 / 1024).toFixed(2) : '0';
    },
  },

  methods: {
    openPicker() {
      this.$refs.input.click();
    },
    onDragOver() {
      this.dragging = true;
    },
    onDragLeave() {
      this.dragging = false;
    },
    onDrop(e) {
      this.dragging = false;
      const file = e.dataTransfer.files[0];
      if (file) this.$emit('file-selected', file);
    },
    onChange(e) {
      const file = e.target.files[0];
      if (file) this.$emit('file-selected', file);
      e.target.value = '';
    },
  },
};
</script>

<style lang="scss" module>

.zone {
  flex: 1;
  aspect-ratio: 1 / 1;
  border: 2px dashed $gray;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 16px;
  position: relative;
  transition: border-color 0.2s, background-color 0.2s;
  background-color: $grey-50;
  user-select: none;
  overflow: hidden;

  &:hover {
    border-color: $primary;
    background-color: $green-50;
  }
}

.zoneDragging {
  border-color: $primary;
  background-color: $green-50;
}

.zoneError {
  border-color: $danger;
  background-color: lighten($danger, 37%);

  &:hover {
    border-color: $danger;
    background-color: lighten($danger, 35%);
  }
}

.zoneHasFile {
  border-style: solid;
  border-color: $primary;
  background-color: $green-50;
}

.hiddenInput {
  display: none;
}

.placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
}

.uploadIcon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: $green-100;
  color: $green-700;
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.uploadLabel {
  font-weight: 600;
  font-size: 14px;
  color: $black;
}

.uploadHint {
  font-size: 13px;
  color: $grey-900;
}

.uploadFormats {
  font-size: 11px;
  color: $grey-900;
}

.fileInfo {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;
  width: 100%;
}

.previewImg {
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
}

.fileIcon {
  font-size: 32px;
  line-height: 1;
}

.fileName {
  font-size: 13px;
  font-weight: 600;
  color: $black;
  word-break: break-all;
  max-width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.fileSize {
  font-size: 12px;
  color: $grey-700;
}

.removeBtn {
  margin-top: 4px;
  background: none;
  border: 1px solid $black-75;
  color: $black-75;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 16px;
  line-height: 1.4;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    border-color: $black-100;
    color: $black-100;
  }
}

.errorMsg {
  position: absolute;
  bottom: 10px;
  font-size: 11px;
  color: $danger;
  text-align: center;
}
</style>
